import React, { useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { keyframes } from "styled-components"
import styled from "~/ui/styled"
import { useSearchParams } from "react-router-dom"
import {
  BetweenFlex,
  Button,
  CenterFlex,
  ColumnFlex,
  Text,
} from "~components/El"
import { useTheme } from "~/ui/styled"
import { useStores } from "~models"
import { globalToast } from "~utils"
import { ActivityIndicator } from "~/ui/primitives"
import { NotificationCenterContent } from "~components/PopupComponents/DialogContent"
import useNavigation from "~hooks/useNavigation"
import { RedpacketStatus, TransferStatus } from "~proto"
import { MODAL_MAX_WIDTH } from "~ui/app-shell"
import PcDetailWatermark from "~router/PcDetailWatermark"
import NoticeRechargeSuccessCard, { isRechargeNotice } from "./notice-recharge-success-card"
import NoticeMaintenanceCard, { isMaintenanceNotice } from "./notice-maintenance-card"
import NoticePendingCollectCard from "./notice-pending-collect-card"
import {
  isPendingCollectNotice,
  resolveNoticeChatNavigation,
} from "./notice-collect-navigation"
import {
  getNoticeSectionIcon,
  useNoticeCenter,
  type NoticeItem,
} from "./notice-center-context"

const centerIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`

const PresentationHost = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--pc-detail-empty-bg);
`

const CenterStage = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
`

const WatermarkLayer = styled.div`
  grid-area: 1 / 1;
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  pointer-events: none;
`

const CardWrap = styled.div`
  grid-area: 1 / 1;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: none;
  min-height: 0;
  overflow: auto;
`

const NoticeCard = styled(ColumnFlex)`
  width: min(${MODAL_MAX_WIDTH}px, 100%);
  max-height: min(70vh, calc(100% - 48px));
  pointer-events: auto;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  animation: ${centerIn} 0.24s ease;
`

const RechargeCardWrap = styled(CardWrap)`
  overflow: visible;
  align-items: center;
  justify-content: flex-start;
`

const RechargeNoticeCard = styled(NoticeCard)`
  overflow: visible;
  background: transparent;
  box-shadow: none;
  max-height: none;
  padding: 0;
`

const CardBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 24px;
`

const CardFooter = styled(BetweenFlex)`
  flex-shrink: 0;
  padding: 16px 24px 20px;
  gap: 12px;
`

const getConfirmText = (item: NoticeItem, data?: any) => {
  if (
    [2, 4].includes(Number(item.type)) &&
    item?.extra?.redpacket_id &&
    data?.status === RedpacketStatus.ACTIVE
  ) {
    return "领取红包"
  }
  if (
    [2, 4].includes(Number(item.type)) &&
    item.extra?.order_no &&
    data?.status === TransferStatus.PENDING
  ) {
    return "转账确认"
  }
  return "标记已读"
}

const NoticeCenterDetail: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { apiStore, notificationStore, chatStore, messageStore } = useStores()
  const [searchParams, setSearchParams] = useSearchParams()
  const noticeId = searchParams.get("notice_id")
  const { getNoticeById, markNoticeReadLocally } = useNoticeCenter()

  const item = getNoticeById(noticeId)
  const [detailData, setDetailData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [detailReady, setDetailReady] = useState(false)

  const closeDetail = useCallback(() => {
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  useEffect(() => {
    setDetailData(null)
    setDetailReady(false)
    if (!item) return

    const loadDetail = async () => {
      if ([2, 4].includes(Number(item.type)) && item.extra?.redpacket_id) {
        setLoading(true)
        try {
          const res = await apiStore.getRedpacketDetail({
            redpacket_id: item.extra.redpacket_id,
          })
          setDetailData(res.data)
          setDetailReady(true)
        } catch (err: any) {
          globalToast.show(err?.message || "获取红包详情失败")
        } finally {
          setLoading(false)
        }
        return
      }

      if ([2, 4].includes(Number(item.type)) && item.extra?.order_no) {
        setLoading(true)
        try {
          const res = await apiStore.getTransferDetail({ order_no: item.extra.order_no })
          setDetailData(res.data)
          setDetailReady(true)
        } catch (err: any) {
          globalToast.show(err?.message || "获取转账详情失败")
        } finally {
          setLoading(false)
        }
        return
      }

      setDetailReady(true)
    }

    loadDetail()
  }, [apiStore, item])

  const markAsRead = useCallback(async () => {
    if (!item) return false
    const res = await apiStore.markNotificationAsRead({ id: item.id })
    if (res) {
      markNoticeReadLocally(item.id)
      notificationStore.refreshUserUnreadNotificationCount()
    }
    return !!res
  }, [apiStore, item, markNoticeReadLocally, notificationStore])

  const navigateToNoticeMessage = useCallback(async () => {
    if (!item) return

    const target = resolveNoticeChatNavigation(item, detailData, chatStore, messageStore)
    if (!target?.chat_id) {
      globalToast.show("无法定位聊天消息")
      return
    }

    const chat = chatStore.getChat(target.chat_id)
    if (chat) chatStore.setCurrent(chat)

    await markAsRead()
    closeDetail()

    navigation.navigate("Chat", {
      chat_id: target.chat_id,
      scrollToMessageId: target.scrollToMessageId,
    })
  }, [item, detailData, chatStore, messageStore, markAsRead, closeDetail, navigation])

  const onConfirm = useCallback(async () => {
    if (!item) return

    if (isRechargeNotice(item)) {
      await markAsRead()
      closeDetail()
      return
    }

    if (isMaintenanceNotice(item)) {
      const ok = await markAsRead()
      if (ok) {
        globalToast.show("标记已读成功")
        closeDetail()
      }
      return
    }

    if (isPendingCollectNotice(item, detailData)) {
      await navigateToNoticeMessage()
      return
    }

    const ok = await markAsRead()
    if (ok) {
      globalToast.show("标记已读成功")
      closeDetail()
    }
  }, [item, detailData, markAsRead, closeDetail, navigateToNoticeMessage])

  const showGenericCard =
    !!noticeId &&
    !!item &&
    !isRechargeNotice(item) &&
    !isMaintenanceNotice(item) &&
    !isPendingCollectNotice(item, detailData)

  const showRechargeCard =
    !!noticeId && !!item && isRechargeNotice(item) && detailReady && !loading

  const showMaintenanceCard =
    !!noticeId && !!item && isMaintenanceNotice(item) && detailReady && !loading

  const showPendingCollectCard =
    !!noticeId &&
    !!item &&
    isPendingCollectNotice(item, detailData) &&
    detailReady &&
    !loading

  const confirmText = item ? getConfirmText(item, detailData) : ""
  const showConfirm =
    !!item &&
    item.is_read === false &&
    !isRechargeNotice(item) &&
    !isMaintenanceNotice(item) &&
    !isPendingCollectNotice(item, detailData)

  return (
    <PresentationHost>
      <CenterStage>
        <WatermarkLayer>
          <CenterFlex flex={1}>
            <PcDetailWatermark />
          </CenterFlex>
        </WatermarkLayer>

        {showPendingCollectCard ? (
          <CardWrap>
            <NoticeCard height="auto" width="auto">
              <NoticePendingCollectCard
                item={item}
                detailData={detailData}
                onClose={closeDetail}
                onConfirm={navigateToNoticeMessage}
              />
            </NoticeCard>
          </CardWrap>
        ) : null}

        {showMaintenanceCard ? (
          <CardWrap>
            <NoticeCard height="auto" width="auto">
              <NoticeMaintenanceCard
                item={item}
                onClose={closeDetail}
                onMarkRead={onConfirm}
                showMarkRead={item.is_read === false}
              />
            </NoticeCard>
          </CardWrap>
        ) : null}

        {showRechargeCard ? (
          <RechargeCardWrap>
            <RechargeNoticeCard height="auto" width="auto">
              <NoticeRechargeSuccessCard item={item} onConfirm={onConfirm} />
            </RechargeNoticeCard>
          </RechargeCardWrap>
        ) : null}

        {showGenericCard ? (
          <CardWrap>
            <NoticeCard height="auto" width="auto">
              <CardBody>
                {loading ? (
                  <CenterFlex padding="40px 0">
                    <ActivityIndicator color={theme.colors.primary} />
                  </CenterFlex>
                ) : detailReady ? (
                  <NotificationCenterContent
                    {...item}
                    icon={getNoticeSectionIcon(item.type)}
                  />
                ) : null}
              </CardBody>
              <CardFooter height="auto">
                <Button
                  flex={1}
                  height="44px"
                  borderRadius="8px"
                  bg={theme.colors.background}
                  onClick={closeDetail}
                >
                  <Text color={theme.colors.text}>关闭</Text>
                </Button>
                {showConfirm ? (
                  <Button
                    flex={1}
                    height="44px"
                    borderRadius="8px"
                    bg={theme.colors.primary}
                    onClick={onConfirm}
                  >
                    <Text color={theme.colors.blockBackground}>{confirmText}</Text>
                  </Button>
                ) : null}
              </CardFooter>
            </NoticeCard>
          </CardWrap>
        ) : null}

        {noticeId &&
        item &&
        loading &&
        !showGenericCard &&
        !showRechargeCard &&
        !showMaintenanceCard &&
        !showPendingCollectCard ? (
          <CardWrap>
            <CenterFlex pointerEvents="auto">
              <ActivityIndicator color={theme.colors.primary} />
            </CenterFlex>
          </CardWrap>
        ) : null}
      </CenterStage>
    </PresentationHost>
  )
}

export default observer(NoticeCenterDetail)
